/** The network that a link crosses. */
export type FlowZone = 'internet' | 'overlay' | 'local';

export const FLOW_ZONE_LABELS: Record<FlowZone, string> = {
    internet: 'Public Internet',
    overlay: 'Ziti Overlay',
    local: 'Local Network',
};

/** A grid placement: the first grid line and the number of tracks. The same numbers work for columns and rows. */
export interface FlowSpan {
    start: number;
    span: number;
}

export interface FlowBand extends FlowSpan {
    zone: FlowZone;
    label: string;
}

export interface FlowLayout {
    trackCount: number;
    /** Track sizes for the wide layout, where tracks are columns. */
    columns: string;
    /** Track sizes for the stacked layout, where tracks are rows. */
    rows: string;
    nodes: FlowSpan[];
    links: FlowSpan[];
    bands: FlowBand[];
}

const NODE_HALF = 'minmax(4rem, 1fr)';
const LINK = 'minmax(5.5rem, 1fr)';
// Sized by the stylesheet, so a flow without zones gets no edge.
const EDGE = 'var(--flow-edge)';

/**
 * Places the nodes and links on one track line: each node takes two half tracks, each link one, with an edge track at each end.
 * A zone band runs from the center of the node before its links to the center of the node after them,
 * so a gateway node sits on the divider. The first and the last band run over the edge tracks.
 */
export function layoutFlow(nodeCount: number, zones: (FlowZone | undefined)[]): FlowLayout {
    const linkCount = Math.max(nodeCount - 1, 0);
    const trackCount = nodeCount > 0 ? 3 * nodeCount + 1 : 0;
    const nodes = Array.from({ length: nodeCount }, (_, i) => ({ start: 3 * i + 2, span: 2 }));
    const links = Array.from({ length: linkCount }, (_, i) => ({ start: 3 * i + 4, span: 1 }));
    const tracks = Array.from({ length: nodeCount }, (_, i) =>
        i < linkCount ? `${NODE_HALF} ${NODE_HALF} ${LINK}` : `${NODE_HALF} ${NODE_HALF}`
    ).join(' ');
    const columns = nodeCount > 0 ? `${EDGE} ${tracks} ${EDGE}` : '';
    const rows = nodeCount > 0 ? `${EDGE} repeat(${trackCount - 2}, auto) ${EDGE}` : '';

    const bands: FlowBand[] = [];
    let from = 0;
    while (from < linkCount) {
        const zone = zones[from];
        let to = from;
        while (to + 1 < linkCount && zones[to + 1] === zone) {
            to++;
        }
        if (zone) {
            const start = from === 0 ? 1 : 3 * from + 3;
            const end = to === linkCount - 1 ? trackCount + 1 : 3 * to + 6;
            bands.push({ zone, label: FLOW_ZONE_LABELS[zone], start, span: end - start });
        }
        from = to + 1;
    }
    return { trackCount, columns, rows, nodes, links, bands };
}
